import { convertFromRaw, EditorState } from 'draft-js';
import { cloneDeep, isNil, isString } from 'lodash';
import { CellData, HotComputedCellValue } from '../../../../../../common/table/TableTypes';
import { CellTypeNames, FormatOptions } from '../../../../../../common/table/CellTypeConstants';
import { isCellTypeNumeric } from '../../../../../../common/table/utils/tableCellDataPropertyUtils';
import {
    getDateFormattingOptions,
    getDateTimeValuesFromInputString,
} from '../../../../../../common/table/utils/tableInputDateTimeUtils';
import { stringShouldFormatAsType } from '../../../../../../common/table/utils/tableInputGeneralUtils';
import rawGetText from '../../../../../../common/utils/editor/rawUtils/rawGetText';

import { getCellRenderValue } from './tableCellRenderValueUtils';
import { cellCanBeRichFormatted, getDefaultTypeObject } from './tableCellTypeModelUtils';

/**
 * Returns whether a type change is valid for the current cell value.
 */
export const canUpdateCellType = (
    normalisedComputedCellValue: HotComputedCellValue,
    newCellType: CellTypeNames,
    locale: string,
): boolean => {
    if ([CellTypeNames.TEXT, CellTypeNames.AUTO].includes(newCellType) || isNil(normalisedComputedCellValue)) {
        return true;
    }

    const rawText = isString(normalisedComputedCellValue)
        ? normalisedComputedCellValue
        : String(normalisedComputedCellValue);

    if (newCellType === CellTypeNames.CHECKBOX) {
        return rawText?.toLowerCase() === 'true' || rawText?.toLowerCase() === 'false';
    }

    const { newCellType: inferredType } = stringShouldFormatAsType(rawText, false, locale) || {};
    if (!inferredType) return false;
    if (newCellType === inferredType) return true;

    return isCellTypeNumeric(newCellType) && isCellTypeNumeric(inferredType);
};

/**
 * Returns the updated cell model for a requested type change.
 */
export const updateCellType = (
    prevCellData: CellData,
    cellTypeName: CellTypeNames,
    currencyPreference: string,
    locale: string,
    normalisedComputedCellValue: HotComputedCellValue,
): CellData => {
    const newCellData = cloneDeep(prevCellData);

    if (prevCellData.type?.name === cellTypeName) return newCellData;

    if (cellTypeName === CellTypeNames.AUTO) {
        newCellData.type = undefined;
        return newCellData;
    }

    if (!canUpdateCellType(normalisedComputedCellValue, cellTypeName, locale)) return prevCellData;

    newCellData.type = {
        ...getDefaultTypeObject(cellTypeName),
        ...(cellTypeName === CellTypeNames.CURRENCY && { [FormatOptions.CURRENCY]: currencyPreference }),
    };

    if (cellTypeName === CellTypeNames.TEXT && prevCellData.type?.name) {
        newCellData.value = getCellRenderValue(prevCellData, locale);
    }

    if (newCellData.type.name === CellTypeNames.DATE_TIME) {
        const rawText = newCellData.draftContent ? rawGetText(newCellData.draftContent) : newCellData.value || '';
        const dateValues = getDateTimeValuesFromInputString(rawText, locale);

        if (dateValues) {
            const { hasTime, hasSeconds, dateFormat } = dateValues;
            newCellData.value = rawText;
            newCellData.type = { ...newCellData.type, hasTime, hasSeconds, dateFormat };

            const format = getDateFormattingOptions(newCellData.value, newCellData.type, {
                prevCellValue: null,
                prevTypeObject: prevCellData.type,
                locale,
            });
            newCellData.type = { ...newCellData.type, ...format };
        }
    }

    if (cellCanBeRichFormatted(prevCellData) && !cellCanBeRichFormatted(newCellData)) {
        if (newCellData.draftContent) {
            newCellData.value = rawGetText(newCellData.draftContent);
            newCellData.textStyle = EditorState.createWithContent(convertFromRaw(newCellData.draftContent))
                .getCurrentInlineStyle()
                .toArray();
        }

        delete newCellData.draftContent;
    }

    return newCellData;
};
